/*
 * Limoo motion — a thin, brand-tokenized wrapper around motion.dev.
 *
 * Brand contract: short durations (160–360ms), out-eased curves only
 * (no springs, no overshoot). Small distances (4–8px slides). Honor
 * prefers-reduced-motion globally — animations no-op when reduced.
 *
 * Public surface:
 *   - fadeUp(el, opts)       — subtle opacity + Y slide-in
 *   - pulse(el, opts)        — small scale bump for state-change feedback
 *   - staggerIn(nodes, opts) — fade+slide a list with even cadence
 *   - exit(el, opts)         — fade + slide out (returns a Promise)
 *   - scrollReveal()         — wires up `[data-reveal]` opt-in across the page
 *   - bumpCartCount(el)      — used by cart-drawer.js when count increases
 *
 * Wiring:
 *   - Loaded as `<script type="module">` from layout/theme.liquid (deferred
 *     by virtue of being a module — runs after parse).
 *   - On DOMContentLoaded, registers global listeners (scroll reveal,
 *     `cart:line-removed`, `cart:count-bump` events).
 *   - Section refresh hook re-runs scroll-reveal so newly-injected sections
 *     animate in too.
 */

import { animate, stagger, inView } from 'https://esm.sh/motion@11';

const root = document.documentElement;
const styles = getComputedStyle(root);

// Read brand tokens at module load. Falls back to sensible defaults so
// the module is resilient to css-variables.liquid being absent.
const tokenMs = (name, fallback) => {
  const raw = styles.getPropertyValue(name).trim();
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
};
const HOVER_MS = tokenMs('--limoo-duration-hover', 200);
const LAYOUT_MS = tokenMs('--limoo-duration-layout', 320);
const DRAWER_MS = tokenMs('--limoo-drawer-duration', 360);
// motion.dev expects seconds, not ms.
const ms = (n) => n / 1000;

// Material-style out-ease — same curve as `--limoo-ease`.
const EASE = [0.4, 0, 0.2, 1];

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isReduced = () => reducedMotion.matches;

/**
 * Fade in + slide up. Default distance 8px, duration --limoo-duration-layout.
 * Returns the Motion animation handle (or undefined if reduced-motion is on).
 */
export function fadeUp(el, opts = {}) {
  if (isReduced() || !el) return;
  const { distance = 8, duration = LAYOUT_MS, delay = 0 } = opts;
  return animate(
    el,
    { opacity: [0, 1], y: [distance, 0] },
    { duration: ms(duration), ease: EASE, delay: ms(delay) }
  );
}

/**
 * Scale-bump for state-change feedback (cart count, success states).
 * 0.92 → 1.06 → 1 over 320ms. Subtle by design.
 */
export function pulse(el, opts = {}) {
  if (isReduced() || !el) return;
  const { from = 0.92, peak = 1.06, duration = LAYOUT_MS } = opts;
  return animate(
    el,
    { scale: [from, peak, 1] },
    { duration: ms(duration), ease: EASE }
  );
}

/**
 * Stagger a list of elements into view. Each child fades + slides 6px up
 * with a 40ms gap between siblings. Use when a freshly rendered list
 * (cart lines, search results) becomes visible.
 */
export function staggerIn(nodes, opts = {}) {
  if (isReduced() || !nodes || nodes.length === 0) return;
  const { distance = 6, duration = HOVER_MS + 80, gap = 0.04 } = opts;
  return animate(
    nodes,
    { opacity: [0, 1], y: [distance, 0] },
    { duration: ms(duration), ease: EASE, delay: stagger(gap) }
  );
}

/**
 * Fade + slide-right exit, useful before removing an item from the DOM.
 * Returns a Promise that resolves when the exit completes — caller can
 * `await` it before mutating the DOM.
 */
export function exit(el, opts = {}) {
  if (isReduced() || !el) return Promise.resolve();
  const { distance = 24, duration = HOVER_MS + 80 } = opts;
  return animate(
    el,
    { opacity: [1, 0], x: [0, distance] },
    { duration: ms(duration), ease: EASE }
  ).finished;
}

/**
 * Scroll-reveal for editorial sections. Any element with [data-reveal]
 * starts hidden and fades up the first time it enters the viewport. Idempotent
 * — safe to call multiple times (e.g., after section refresh).
 */
export function scrollReveal() {
  if (isReduced()) return;
  document.querySelectorAll('[data-reveal]:not([data-reveal-done])').forEach((el) => {
    el.dataset.revealDone = 'true';
    // Pre-set the start state inline so there's no flash before inView fires.
    el.style.opacity = '0';
    inView(
      el,
      () => {
        animate(
          el,
          { opacity: [0, 1], y: [8, 0] },
          { duration: ms(LAYOUT_MS + 80), ease: EASE }
        );
      },
      { amount: 0.15 }
    );
  });
}

/**
 * Used by cart-drawer.js when the count increases. Same scale-bump as
 * pulse() but the existing CSS `--bump` keyframe also runs in parallel —
 * Motion is the visible animation; the class flag survives in case CSS
 * is preferred at any point.
 */
export function bumpCartCount(el) {
  return pulse(el, { from: 0.85, peak: 1.12, duration: DRAWER_MS - 40 });
}

// ---------- Wiring ----------

function init() {
  scrollReveal();

  // Cart drawer: stagger items in whenever the drawer becomes [data-state="open"].
  // The drawer dispatches `cart:updated` after a refresh — re-run stagger then too.
  document.addEventListener('cart:drawer-open', (e) => {
    const drawer = e.target;
    if (!(drawer instanceof Element)) return;
    const lines = drawer.querySelectorAll('[data-cart-items] > .limoo-cart-line');
    staggerIn(lines);
  });
  document.addEventListener('cart:updated', (e) => {
    const drawer = e.target;
    if (!(drawer instanceof Element)) return;
    const lines = drawer.querySelectorAll('[data-cart-items] > .limoo-cart-line');
    staggerIn(lines);
  });

  // Cart count bump — fired by cart-drawer.js after refresh
  document.addEventListener('cart:count-bump', (e) => {
    if (e.target instanceof Element) bumpCartCount(e.target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Shopify Theme Editor hot-reloads sections; pick up new [data-reveal] nodes.
document.addEventListener('shopify:section:load', () => scrollReveal());

// Expose a tiny global for ad-hoc use from inline scripts that don't import
// (e.g. a future PDP add-to-cart hook). The full API stays the named exports.
window.LimooMotion = { fadeUp, pulse, staggerIn, exit, scrollReveal, bumpCartCount };
